import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, apiError } from '@/lib/pos-api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { shortcutBinding, shortcutCatalog, shortcutTarget } from '@/lib/user-shortcuts.mjs';
import ShortcutBindingsEditor from '@/components/ShortcutBindingsEditor';

export default function UserShortcuts({ groups, open, setOpen }) {
  const navigate = useNavigate();
  const catalog = useMemo(() => shortcutCatalog(groups), [groups]);
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [policy, setPolicy] = useState(null);
  const locked = policy?.enabled && !policy?.allow_personal;
  useEffect(() => {
    let active = true;
    setBusy(true); setError(''); setSaved(false);
    let inFlight = false;
    const load = async (initial = false) => {
      if (inFlight || (!initial && document.visibilityState === 'hidden')) return;
      inFlight = true;
      try {
        const { data } = await api.get('/auth/shortcut-context');
        if (active) { setSettings(data.effective); setPolicy(data.policy); if (initial) setDraft(data.personal); }
      } catch (e) { if (active && initial) setError(apiError(e)); }
      finally { inFlight = false; if (active && initial) setBusy(false); }
    };
    load(true);
    const refresh = () => load();
    const timer = setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    window.addEventListener('pos-shortcuts-updated', refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('pos-shortcuts-updated', refresh); };
  }, [open]);
  useEffect(() => {
    const consumedModifiers = new Set();
    const keydown = event => {
      if (!shortcutBinding(event, settings, catalog)) return;
      const target = shortcutTarget(event, settings, catalog, open || !!document.querySelector('[role="dialog"], [role="alertdialog"], dialog[open]'));
      // An overridden key must never also execute its old cashier/browser action,
      // including when navigation is blocked inside a form or dialog.
      event.preventDefault(); event.stopImmediatePropagation();
      if (event.ctrlKey) consumedModifiers.add('Control');
      if (event.shiftKey) consumedModifiers.add('Shift');
      if (event.altKey) consumedModifiers.add('Alt');
      if (target) navigate(target);
    };
    const keyup = event => {
      if (!consumedModifiers.delete(event.key)) return;
      event.preventDefault(); event.stopImmediatePropagation();
    };
    window.addEventListener('keydown', keydown, true);
    window.addEventListener('keyup', keyup, true);
    return () => { window.removeEventListener('keydown', keydown, true); window.removeEventListener('keyup', keyup, true); };
  }, [settings, catalog, open, navigate]);
  const save = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      const { data } = await api.put('/auth/shortcuts', locked ? { ...draft, use_store: true } : draft);
      setDraft(data); setSaved(true);
      window.dispatchEvent(new Event('pos-shortcuts-updated'));
    } catch (e) { setError(apiError(e)); }
    finally { setBusy(false); }
  };
  return <>
    <Dialog open={open} onOpenChange={value => { if (!busy) setOpen(value); }}>
      <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>Pengaturan Shortcut Saya</DialogTitle><DialogDescription>Tersimpan untuk akun Anda. Pilih menu sesuai hak akses; tidak mengubah shortcut pengguna lain.</DialogDescription></DialogHeader>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {draft && <fieldset disabled={busy} className="space-y-3">
          <label className="flex gap-2 items-center"><input type="checkbox" disabled={locked} checked={locked || draft.use_store === true} onChange={e => { setDraft({ ...draft, use_store: e.target.checked }); setSaved(false); }} />Ikuti pengaturan toko</label>
          {locked && <p className="text-sm">Admin mengunci shortcut toko. Pengaturan pribadi tidak digunakan.</p>}
          {(locked || draft.use_store) && <div className="text-sm rounded border p-3"><p className="font-semibold">Shortcut toko {policy?.enabled ? 'aktif' : 'belum diaktifkan — menggunakan default sistem'}</p>{policy?.enabled && policy.bindings.map(item => <p key={item.key}>{item.key} → {catalog.find(menu => menu.to === item.to)?.label || 'Menu tidak tersedia untuk hak akses Anda'}</p>)}</div>}
          <fieldset disabled={locked || draft.use_store === true} className="space-y-3">
          <label className="flex gap-2 items-center"><input type="checkbox" checked={draft.enabled} onChange={e => { setDraft({ ...draft, enabled: e.target.checked, use_store: false }); setSaved(false); }} />Aktifkan shortcut menu pribadi</label>
          <ShortcutBindingsEditor value={draft} onChange={value => { setDraft(value); setSaved(false); }} catalog={catalog} />
          </fieldset>
          <Button onClick={save} disabled={busy}>{busy ? 'Memuat / menyimpan…' : 'Simpan Shortcut'}</Button>
          <Button type="button" variant="outline" className="ml-2" disabled={busy || locked} onClick={() => { setDraft({ ...draft, enabled: false, bindings: [], use_store: false }); setSaved(false); }}>Reset semua ke default</Button>
          <p className="text-xs text-ui-muted">Pilih Default sistem untuk mengembalikan satu tombol. Reset semua mengembalikan seluruh tombol. Tekan Simpan Shortcut untuk menerapkan.</p>
        </fieldset>}
        {saved && <p role="status" className="text-sm text-green-700">Shortcut tersimpan untuk akun Anda.</p>}
        <div className="text-sm space-y-2 border-t pt-3">
          <p className="font-semibold">Catatan tombol yang sudah digunakan</p>
          <p>F1: Produk (admin) · F2: Kasir / pencarian · F3: Pelanggan · F4: Keranjang · F8: Pembayaran.</p>
          <p>Enter: lanjut / simpan sesuai formulir · Esc: tutup popup · Control: aksi kasir sesuai posisi · Shift: varian · −: kurangi QTT sesuai posisi.</p>
          <p>F5: muat ulang · F11: layar penuh. Fungsi bawaan berlaku jika tombol memakai Default sistem atau shortcut pribadi dinonaktifkan.</p>
          <p>Shortcut pribadi hanya membuka menu, bukan posting transaksi. Saat kursor di kolom input atau popup terbuka, tombol yang diganti ditahan agar tidak menjalankan fungsi lama. Pilih area kosong dahulu untuk membuka menu.</p>
          <p>F6/F7/F10/F12 dapat digunakan browser atau perangkat. F12 mungkin memerlukan Fn; jika ditangkap perangkat, pilih tombol lain. Satu tombol hanya memiliki satu tujuan per akun.</p>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
